import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { stripeAPI } from '../../api/services/stripe'
import Alert from '../../components/common/Alert/Alert.tsx'
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs.tsx'
import Button from '../../components/common/Button/Button.tsx'
import Card from '../../components/common/Card/Card.tsx'
import Loader from '../../components/common/Loader/Loader.tsx'
import StatusBar from '../../components/stripe/StatusBar/StatusBar.tsx'
import { usePanelUserRights } from '../../hooks/usePanelUserRights.ts'
import useStripeAccountInfo from '../../hooks/useStripeAccountInfo.ts'

const StripeAccount = () => {
  const [isAccountCreated, setIsAccountCreated] = useState<boolean | null>(null)
  const [stripeLink, setStripeLink] = useState('')
  const [errorText, setErrorText] = useState('')
  const [createAccountEnded, setCreateAccountsEnded] = useState<boolean | null>(
    null
  )
  const [onBoardClicked, setOnBoardClicked] = useState(0)
  const [loading, setLoading] = useState(false)
  const { hasMainUserRights } = usePanelUserRights()

  const [currentStep, setCurrentStep] = useState(1)
  const [requirementsStripeFilled, setRequirementsStripeFilled] = useState<
    boolean | null
  >(null)

  const { stripeAccountCompleted } = useStripeAccountInfo()

  if (stripeAccountCompleted) {
    currentStep === 2 && setCurrentStep(3)
  }

  const createAccount = async () => {
    const toastId = toast.loading('Tworzenie konta... Proszę czekać.')
    try {
      setLoading(true)
      setCreateAccountsEnded(false)
      const response = await stripeAPI.createExpressAccount()
      if (response.status === 200) {
        setIsAccountCreated(true)
        setCurrentStep(2)
      }
      toast.update(toastId, {
        render: 'Konto zostało stworzone. Przejdź do aktywacji konta.',
        type: 'success',
        isLoading: false,
        autoClose: 4000
      })
    } catch {
      toast.dismiss(toastId)
    } finally {
      setCreateAccountsEnded(true)
    }
  }

  const getOnboardLink = async () => {
    try {
      setLoading(true)
      const response = await stripeAPI.getOnboardLink()
      if (response.status === 200) {
        setStripeLink(response.data.url)
        setIsAccountCreated(true)
        setCurrentStep(2)
      } else if (response.status === 400) {
        if (response.error === 1016) {
          // First you need to create a ddpanel-sub-account for the user
          setErrorText(
            'Ten użytkownik nie został dodany do obsługi płatności. Skontaktuj się z administratorem twoich danych.'
          )
        } else if (response.error === 1017) {
          // First you need to create a Stripe account for the user
          setIsAccountCreated(false)
        }
      } else if (response.status === 500) {
        setErrorText(
          'Ten użytkownik nie został dodany do obsługi płatności lub podano błędne dane konta. Skontaktuj się z administratorem twoich danych.'
        )
      }
    } catch (error) {
      console.log('createLink error: ', error)
    } finally {
      setLoading(false)
    }
  }

  const activateAccount = () => {
    setOnBoardClicked(onBoardClicked + 1)
    window.open(stripeLink, '_blank')
  }

  useEffect(() => {
    if (
      (isAccountCreated && onBoardClicked > 0) ||
      (isAccountCreated && stripeLink === '')
    ) {
      !hasMainUserRights && getOnboardLink()
    }
  }, [isAccountCreated, onBoardClicked])

  const checkStripeRequirements = async () => {
    try {
      const requirementsResponse = await stripeAPI.requirements_filled()
      if (requirementsResponse.status === 200) {
        requirementsResponse.data.requirements_filled
          ? setRequirementsStripeFilled(true)
          : setRequirementsStripeFilled(false)
      }
    } catch (error) {
      console.log('required error: ', error)
    }
  }

  useEffect(() => {
    !hasMainUserRights && getOnboardLink()
    hasMainUserRights && setCurrentStep(3)
  }, [])

  useEffect(() => {
    requirementsStripeFilled === null &&
      currentStep === 3 &&
      !hasMainUserRights &&
      checkStripeRequirements()
  }, [currentStep])

  return (
    <>
      <Breadcrumbs pageName="Konto Stripe" breadcrumbs={[]} />
      <Card>
        <Card.Body>
          {!errorText && !loading && <StatusBar currentStep={currentStep} />}

          {loading && <Loader label={'Pobieranie danych o koncie...'} />}

          {errorText && (
            <div className="mb-8">
              <p className="text-xl font-bold mb-6 uppercase">
                Brak założonego konta.
              </p>
              <Alert type="warning" message={errorText} />
            </div>
          )}

          {currentStep === 1 &&
            isAccountCreated !== null &&
            !isAccountCreated && (
              <>
                <Alert
                  className="my-6"
                  type="info"
                  title="Brak założonego konta"
                  message="Konto na platformie płatniczej Stripe jest wymagane do przyjmowania płatności w celu realizacji zamówień od przyjmowania płatności w celu realizacji zamówień od pacjentów"
                />

                <Button
                  variant="filled"
                  size="medium"
                  color="primary"
                  type="button"
                  onClick={() => createAccount()}
                  disabled={createAccountEnded === false}
                >
                  <span className="text-xl">{'Utwórz konto Stripe'}</span>
                </Button>
              </>
            )}

          {currentStep === 2 && stripeLink && (
            <>
              <Alert
                className="my-6"
                type="info"
                title="Konto zostało założone"
                message="Kolejnym etapem jest aktywacja konta na platformie Stripe. Poniższy przycisk przekieruje Ciebie na stronę z formularzem aby uzupełnić dane wymagane do aktywacji konta Stripe."
              />
              <Button
                variant="filled"
                size="medium"
                color="success"
                type="button"
                onClick={() => activateAccount()}
              >
                {'Przejdź do aktywacji konta Stripe'}
              </Button>
            </>
          )}

          {currentStep === 3 && (
            <>
              <div className="flex flex-row my-6 items-center justify-between">
                <p className="text-xl font-bold uppercase">
                  Konto jest aktywne.
                </p>

                {!hasMainUserRights && (
                  <Button
                    variant="filled"
                    size="medium"
                    color="primary"
                    type="button"
                    className="h-12"
                    onClick={() => activateAccount()}
                  >
                    {'Edycja danych konta Stripe'}
                  </Button>
                )}
              </div>

              {requirementsStripeFilled === null && !hasMainUserRights && (
                <Loader label={'Sprawdzam status konta...'} />
              )}

              {requirementsStripeFilled === false && (
                <div>
                  <p className="text-2xl mb-4" style={{ color: '#F00' }}>
                    UWAGA: Wymagana weryfikacja dowodu tożsamości!
                  </p>
                  <p className="text-xl mb-6 p-4 bg-yellow-200">
                    {
                      'W celu zapewnienia dodatkowego bezpieczeństwa, osoba zakładająca konto Stripe powinna dokończyć weryfikację tożsamości z wykorzystaniem dowodu tożsamości wydanego przez organ administracyjny - przejdź do edycji danych konta Stripe do sekcji "Dane osobowe" '
                    }
                  </p>
                </div>
              )}

              {requirementsStripeFilled === true && (
                <p className="text-xl mb-6">
                  {
                    'Twoje konto Stripe jest aktywne co oznacza, że możesz przyjmować płatności za zamówienia od pacjentów.'
                  }
                </p>
              )}
            </>
          )}
        </Card.Body>
      </Card>
    </>
  )
}

export default StripeAccount
